import type { ValidationError } from '@anytracker/core'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { TextField } from '../../components/Field.js'
import { useToast } from '../../components/Toast.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Tags.module.css'

/** The list's own default swatch, so a tag made here looks like the rest. */
const DEFAULT_COLOUR = '#8b939e'

/**
 * Making a tag on purpose (T-917).
 *
 * Tags are normally made **while logging**, in the moment, and that stays the
 * main way — the log flow keeps its `+ New tag`. This is the other door, and it
 * exists because the first one is not always open:
 *
 *   - You cannot log before you have a track, so a first-run user who wants to
 *     set up their tags first has nowhere to do it.
 *   - The Tags screen renames, recolours, merges and **deletes**. Without this,
 *     deleting your last tag is one-way: the only way back is to go and record
 *     something.
 *
 * Create only. A tag is a name and a colour, and the list edits both in place —
 * so there is nothing for an edit screen to do that the list does not already
 * do better, next to the usage count that tells you whether to bother.
 */
export function TagEditorScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [colour, setColour] = useState(DEFAULT_COLOUR)
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [saving, setSaving] = useState(false)

  async function create() {
    const trimmed = name.trim()
    if (trimmed === '' || saving) {
      return
    }
    setErrors([])
    setSaving(true)

    const result = await repos.tags.create(trimmed, colour)

    setSaving(false)
    if (!result.ok) {
      // Shown here rather than as a toast: the name is still on screen and
      // still wrong, and a message that fades leaves the user looking at a
      // field with no idea why the button did nothing.
      setErrors(result.errors)
      return
    }

    show({ message: `Added ${result.value.name}.` })
    void navigate('/tags')
  }

  return (
    <>
      <h1>New tag</h1>

      <div className={styles['editor']}>
        <TextField
          label="Name"
          value={name}
          placeholder="busy, unwell, travelling…"
          onChange={(event) => {
            setName(event.target.value)
            setErrors([])
          }}
        />

        <div className={styles['colourField']}>
          <label className={styles['colourLabel']} htmlFor="tag-colour">
            Colour
          </label>
          <input
            id="tag-colour"
            type="color"
            className={styles['colour']}
            value={colour}
            onChange={(event) => setColour(event.target.value)}
          />
        </div>

        {errors.length > 0 && (
          <div className={styles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code}>{error.message}</span>
            ))}
          </div>
        )}

        <div className={styles['editorActions']}>
          <Button onClick={() => void navigate('/tags')}>Cancel</Button>
          <Button
            variant="primary"
            fullWidth
            disabled={name.trim() === '' || saving}
            onClick={() => void create()}
          >
            Add tag
          </Button>
        </div>
      </div>
    </>
  )
}
